/*
 * Flowtica 游客录音导入发送 SDK（vendored）
 * 来源：https://web-stg-internal.flowtica.co/assets/guest-audio-client.js（2026-10-22 拉取，协议 flowtica.audio-import v1）
 * 远程模块没有 CORS 头，按接入文档「放入自己的工程打包」的方式内置；逻辑未改，仅把 ES module 导出换成全局命名空间。
 * 协议升级时需重新拉取对应部署的 SDK 覆盖本文件。
 */
(function () {
//#region renderer/features/guest-audio-import/client.ts
var PROTOCOL = "flowtica.audio-import";
var VERSION = 1;
var DEFAULT_TIMEOUT_MS = 15e3;
function createGuestAudioImportClient(options) {
	const { iframe, receiverOrigin } = options;
	const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;
	assertOrigin(receiverOrigin);
	if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) throw new RangeError("timeoutMs must be a positive finite number");
	let disposed = false;
	let sessionId = null;
	let readyPayload = null;
	let connectOperation = null;
	const pending = new Map();
	const send = (message) => {
		iframe.contentWindow?.postMessage(message, receiverOrigin);
	};
	const onMessage = (event) => {
		if (event.origin !== receiverOrigin || event.source !== iframe.contentWindow) return;
		if (!isEnvelope(event.data)) return;
		const operation = pending.get(event.data.requestId);
		if (!operation) return;
		if (event.data.type === "ERROR") {
			pending.delete(event.data.requestId);
			if (event.data.payload.code === "INVALID_SESSION") {
				sessionId = null;
				readyPayload = null;
			}
			operation.reject(new GuestAudioImportClientError(event.data.payload.code, event.data.payload.message));
			if (connectOperation === operation) connectOperation = null;
			return;
		}
		if (event.data.type === "READY" && operation.kind === "connect") {
			pending.delete(event.data.requestId);
			sessionId = event.data.payload.sessionId;
			readyPayload = event.data.payload;
			if (connectOperation === operation) connectOperation = null;
			operation.resolve(event.data.payload);
			return;
		}
		if (event.data.type === "AUDIO_SAVED" && operation.kind === "recording") {
			if (event.data.payload.recordingId !== operation.recordingId) return;
			pending.delete(event.data.requestId);
			operation.resolve(event.data.payload);
		}
	};
	const invalidateSession = () => {
		sessionId = null;
		readyPayload = null;
		for (const [requestId, operation] of pending) if (operation.kind === "connect") {
			if (operation.request) send(operation.request);
		} else {
			pending.delete(requestId);
			operation.reject(new Error("The Flowtica iframe reloaded during recording import"));
		}
	};
	const onLoad = () => invalidateSession();
	const onPageShow = (event) => {
		if (event.persisted) invalidateSession();
	};
	window.addEventListener("message", onMessage);
	window.addEventListener("pageshow", onPageShow);
	iframe.addEventListener("load", onLoad);
	const ensureActive = () => {
		if (disposed) throw new Error("Guest audio import client has been disposed");
	};
	return {
		connect() {
			ensureActive();
			if (sessionId && readyPayload) return Promise.resolve(readyPayload);
			if (connectOperation) return connectOperation.promise;
			const request = {
				protocol: PROTOCOL,
				version: VERSION,
				type: "INIT",
				requestId: createRequestId(),
				payload: { connectionId: createRequestId() }
			};
			const operation = createPending("connect", request.requestId, timeoutMs, () => {
				pending.delete(request.requestId);
				if (connectOperation === operation) connectOperation = null;
			});
			operation.request = request;
			connectOperation = operation;
			pending.set(request.requestId, operation);
			send(request);
			return operation.promise;
		},
		sendRecording(recording) {
			ensureActive();
			if (!sessionId) return Promise.reject(new Error("Call connect() before sendRecording()"));
			if (!(recording.audio instanceof Blob)) return Promise.reject(new TypeError("audio must be a Blob"));
			if (!recording.recordingId.trim()) return Promise.reject(new TypeError("recordingId must not be empty"));
			const request = {
				protocol: PROTOCOL,
				version: VERSION,
				type: "AUDIO_PUT",
				requestId: createRequestId(),
				payload: {
					sessionId,
					recordingId: recording.recordingId,
					title: recording.title,
					startedAt: recording.startedAt,
					durationMs: recording.durationMs,
					audio: recording.audio
				}
			};
			const operation = createPending("recording", request.requestId, timeoutMs, () => pending.delete(request.requestId));
			operation.recordingId = recording.recordingId;
			pending.set(request.requestId, operation);
			send(request);
			return operation.promise;
		},
		dispose() {
			if (disposed) return;
			disposed = true;
			window.removeEventListener("message", onMessage);
			window.removeEventListener("pageshow", onPageShow);
			iframe.removeEventListener("load", onLoad);
			for (const operation of pending.values()) operation.reject(new Error("Guest audio import client has been disposed"));
			pending.clear();
			sessionId = null;
			readyPayload = null;
			connectOperation = null;
		}
	};
}
var GuestAudioImportClientError = class extends Error {
	code;
	constructor(code, message) {
		super(message);
		this.code = code;
		this.name = "GuestAudioImportClientError";
	}
};
function assertOrigin(receiverOrigin) {
	try {
		const url = new URL(receiverOrigin);
		if (url.protocol !== "https:" && url.protocol !== "http:" || url.origin !== receiverOrigin) throw new Error();
	} catch {
		throw new TypeError("receiverOrigin must be an exact HTTP(S) origin");
	}
}
function createRequestId() {
	return crypto.randomUUID();
}
function createPending(kind, requestId, timeoutMs, onTimeout) {
	let resolvePromise;
	let rejectPromise;
	const promise = new Promise((resolve, reject) => {
		resolvePromise = resolve;
		rejectPromise = reject;
	});
	const timer = window.setTimeout(() => {
		onTimeout();
		rejectPromise(new Error(`Guest audio import timed out (${requestId})`));
	}, timeoutMs);
	return {
		kind,
		requestId,
		promise: promise.finally(() => window.clearTimeout(timer)),
		resolve: (value) => resolvePromise(value),
		reject: rejectPromise
	};
}
function isEnvelope(value) {
	if (!value || typeof value !== "object") return false;
	const message = value;
	if (message.protocol !== PROTOCOL || message.version !== VERSION || typeof message.requestId !== "string") return false;
	if (!message.payload || typeof message.payload !== "object") return false;
	if (message.type === "ERROR") {
		const payload = message.payload;
		return typeof payload.code === "string" && typeof payload.message === "string";
	}
	if (message.type === "READY") {
		const payload = message.payload;
		return typeof payload.sessionId === "string" && Number.isFinite(payload.maxAudioBytes) && Array.isArray(payload.acceptedMimeTypes);
	}
	if (message.type === "AUDIO_SAVED") {
		const payload = message.payload;
		return typeof payload.recordingId === "string" && typeof payload.localId === "string" && Number.isFinite(payload.savedAt);
	}
	return false;
}
//#endregion

window.FlowticaGuestAudioImport = { createGuestAudioImportClient, GuestAudioImportClientError };
})();
